import { ApplicationConfig, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';

import { routes } from './app.routes';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor, provideKeycloak
} from 'keycloak-angular';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { LanguageService, provideAppLocale, unauthorizedInterceptor } from 'shared-lib';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { orgContextInterceptor } from './auth/org-context.interceptor';
import { environment } from '../environments/environment';
import { ApiModule, Configuration, ConfigurationParameters } from '../api/gen';

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const apiBase = environment.apiUrl.replace(/\/+$/, '');
const apiCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: new RegExp(`^${escapeRegex(apiBase)}/.*$`),
  httpMethods: ['GET', 'POST', 'PUT', 'DELETE'],
});

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    basePath: environment.apiUrl,
  };
  return new Configuration(params);
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([includeBearerTokenInterceptor, orgContextInterceptor, unauthorizedInterceptor])),
    // Dates and money follow the same stored language choice the translations do.
    // Without it Angular formats against en-US while the UI speaks German.
    provideAppLocale(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: '/i18n/', suffix: '.json' }),
      fallbackLang: 'en',
      lang: 'en'
    }),
    // Resolve + load the active language before first render (no flash of keys).
    provideAppInitializer(() => inject(LanguageService).init()),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [apiCondition]
    },
    importProvidersFrom(ApiModule),
    {
      provide: Configuration,
      useFactory: apiConfigFactory
    },
    provideKeycloak({
      config: {
        url: environment.auth.authority,
        realm: environment.auth.realm,
        clientId: environment.auth.clientId
      },
      initOptions: {
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
        redirectUri: window.location.origin + '/'
      },
    })]
};
